import axios from 'axios';
import { call, all, put, takeLatest, delay, select } from 'redux-saga/effects';

import api from '~/services/api';
import notification from '~/services/notification';
import history from '~/services/history';
import domainService from '~/services/modules/domain/domain-service';
import productService from '~/store/modules/product/product-service';
import productKitService from '~/store/modules/product-kit/product-kit-service';
import * as productKitActions from '~/store/modules/product-kit/product-kit-actions';
import * as productActions from '../product/product-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM
} from '~/store/modules/product-kit/product-kit-constants';
import { ISA_ERROR, ORDER_TYPE } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import { AppStateType } from '~/store/modules/root-reducer';

const INITIAL_VALUES = {
  code: 0,
  externalCode: null,
  name: null,
  barCode: null,
  brand: null,
  productsKit: []
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a.externalCode - b.externalCode);
};

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAll() {
  try {
    yield put(productKitActions.setLoadingData(true));

    const { data } = yield call(productKitService.getProductKits);
    if (data) {
      yield put(productKitActions.getAllSuccess([...data]));
    }

    yield put(productKitActions.setLoadingData(false));
  } catch (error) {
    yield put(productKitActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(productKitActions.setLoadingData(true));

    const [{ data: brands }, { data: providers }, { data: departaments }, { data: categories }] = yield all([
      call(domainService.getBrands),
      call(domainService.getProviders),
      call(domainService.getDepartments),
      call(domainService.getSections),
      call(domainService.getCategories)
    ]);

    const selects = {
      brands: sort(brands),
      providers: sort(providers),
      departaments: sort(departaments),
      categories: sort(categories),
    };

    yield queryProducts({});

    if (!id) {
      yield put(
        productKitActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES
          },
          selects
        })
      );
    } else {
      const { data } = yield call(productKitService.getProductKitById, id);

      if (data) {
        yield put(
          productKitActions.getByIdSuccess({
            data: {
              ...data
            },
            selects
          })
        );
      }
    }
    yield put(productKitActions.setLoadingData(false));
  } catch (error) {
    yield put(productKitActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    yield put(productKitActions.setLoadingSend(true));

    const { id, payload } = data;
    yield select((state: AppStateType) => state?.orderType?.form);

    const newPayload = {
      ...payload,
      type: payload?.type ?? ORDER_TYPE.NORMAL,
      paymentPlanExternalCodes: [...payload?.paymentPlans.map(x => x?.externalCode)],
      subsidiaries: [...payload?.subsidiaries.map(x => x.externalCode)],
      team: [...payload?.team.map(item => item._id)]
    };

    if (!id) {
      yield call(productKitService.createProductKit, newPayload);
    } else {
      yield call(productKitService.updateProductKit, newPayload);
    }

    yield call(
      notification.success,
      id ? formatMessage('orderTypeFormSuccessEdit') : formatMessage('orderTypeFormSuccessCreate')
    );
    yield put(productKitActions.setLoadingSend(false));
    yield call(history.push, '/register/product-kit');
  } catch (error) {
    yield put(productKitActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(productKitActions.setLoadingSend(true));
      yield call(productKitService.deleteProductKit, id);
      yield put(productKitActions.setLoadingSend(false));

      yield put(productKitActions.getAllRequest());
      yield call(notification.success, formatMessage('orderTypeFormSuccessCreate'));
    }
  } catch (error) {
    yield put(productKitActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/product-kit/edit/${id}`);
}

export function* queryProducts({ queryText = '' }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productActions.setLoadingData(true));
    const { data: products } = yield call(
      productService.getRawProducts,
      {
        skip: 0,
        limit: 100,
        queryString: queryText
      },
      {
        cancelToken
      }
    );

    yield put(productKitActions.getFilterProductSuccess(products));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
